class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    this.isOpen = false;
    this.abortController = new AbortController();

    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));

    this.addEventListener('keyup', this.onKeyup.bind(this));

    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    /*
      If the query changed direction or was replaced,
      remove stale rendered groups immediately.
    */
    if (
      !this.searchTerm ||
      !newSearchTerm.startsWith(this.searchTerm)
    ) {
      this.querySelector(
        '#predictive-search-results-groups-wrapper'
      )?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortActiveRequest();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const selectedLink =
      this.querySelector('[aria-selected="true"] a');

    if (
      !this.getQuery().length ||
      selectedLink
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';

      this.abortActiveRequest();

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  abortActiveRequest() {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';

    const selectedElement =
      this.querySelector('[aria-selected="true"]');

    const allVisibleElements =
      Array.from(
        this.querySelectorAll(
          'li, button.predictive-search__item'
        )
      ).filter(
        (element) =>
          element.offsetParent !== null
      );

    if (!allVisibleElements.length) return;

    let activeElementIndex = 0;

    if (
      moveUp &&
      !selectedElement
    ) {
      return;
    }

    let selectedElementIndex = -1;

    let i = 0;

    while (
      selectedElementIndex === -1 &&
      i < allVisibleElements.length
    ) {
      if (
        allVisibleElements[i] === selectedElement
      ) {
        selectedElementIndex = i;
      }

      i++;
    }

    this.statusElement.textContent = '';

    if (
      !moveUp &&
      selectedElement
    ) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (
      activeElementIndex === selectedElementIndex
    ) {
      return;
    }

    const activeElement =
      allVisibleElements[activeElementIndex];

    activeElement.setAttribute(
      'aria-selected',
      'true'
    );

    if (selectedElement) {
      selectedElement.setAttribute(
        'aria-selected',
        'false'
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  selectOption() {
    const selectedOption =
      this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]'
      );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    /*
      Critical:
      abort older request before starting the new one.
    */
    this.abortActiveRequest();

    const controller = this.abortController;

    const requestTerm = searchTerm;

    const queryKey =
      requestTerm
        .replace(/\s+/g, '-')
        .toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      /*
        Only render cached result if the input
        still matches the request term.
      */
      if (this.getQuery() === requestTerm) {
        this.renderSearchResults(
          this.cachedResults[queryKey],
          requestTerm
        );
      }

      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        requestTerm
      )}&section_id=predictive-search`,
      {
        signal: controller.signal
      }
    )
      .then((response) => {
        if (!response.ok) {
          const error =
            new Error(
              `Predictive search failed: ${response.status}`
            );

          throw error;
        }

        return response.text();
      })
      .then((text) => {
        /*
          Do not render stale responses.
        */
        if (
          controller.signal.aborted ||
          this.getQuery() !== requestTerm
        ) {
          return;
        }

        const parsedDocument =
          new DOMParser()
            .parseFromString(
              text,
              'text/html'
            );

        const section =
          parsedDocument.querySelector(
            '#shopify-section-predictive-search'
          );

        if (!section) {
          return;
        }

        const resultsMarkup =
          section.innerHTML;

        /*
          Keep all predictive search instances
          sharing the same cache.
        */
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[
              queryKey
            ] = resultsMarkup;
          }
        );

        /*
          Final stale-request protection.
        */
        if (
          this.getQuery() !== requestTerm
        ) {
          return;
        }

        this.renderSearchResults(
          resultsMarkup,
          requestTerm
        );
      })
      .catch((error) => {
        if (
          error.name === 'AbortError' ||
          error?.code === 20
        ) {
          return;
        }

        this.close();

        console.error(error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      );

    if (!this.statusElement) return;

    this.setLiveRegionText(
      this.loadingText
    );

    this.setAttribute(
      'loading',
      true
    );
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText || '';

    setTimeout(() => {
      this.statusElement.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  renderSearchResults(
    resultsMarkup,
    requestTerm
  ) {
    /*
      Never let an old result render.
    */
    if (
      requestTerm &&
      this.getQuery() !== requestTerm
    ) {
      return;
    }

    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute(
      'results',
      'true'
    );

    /*
      Do NOT rewrite "SEARCH FOR" text here.

      predictive-search.liquid already renders
      the exact server-side term.

      This eliminates the broken DD / partial
      replacement behavior.
    */

    this.setLiveRegionResults();

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );

    if (
      countElement &&
      this.statusElement
    ) {
      this.setLiveRegionText(
        countElement.textContent
      );
    }
  }

  getResultsMaxHeight() {
    const header =
      document.querySelector(
        '.section-header'
      );

    const headerBottom =
      header
        ? header.getBoundingClientRect().bottom
        : 0;

    this.resultsMaxHeight =
      Math.max(
        200,
        window.innerHeight - headerBottom - 12
      );

    return this.resultsMaxHeight;
  }

  open() {
    /*
      Let our WATCHERS CSS control the actual
      visual max-height when available.

      Only set a safe inline maximum if needed.
    */
    const maxHeight =
      this.resultsMaxHeight ||
      this.getResultsMaxHeight();

    this.predictiveSearchResults.style.maxHeight =
      `${maxHeight}px`;

    this.setAttribute(
      'open',
      'true'
    );

    this.input.setAttribute(
      'aria-expanded',
      'true'
    );

    this.isOpen = true;
  }

  close(
    clearSearchTerm = false
  ) {
    this.closeResults(
      clearSearchTerm
    );

    this.isOpen = false;
  }

  closeResults(
    clearSearchTerm = false
  ) {
    if (clearSearchTerm) {
      this.input.value = '';

      this.removeAttribute(
        'results'
      );
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        'false'
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute(
      'loading'
    );

    this.removeAttribute(
      'open'
    );

    this.input.setAttribute(
      'aria-expanded',
      'false'
    );

    this.resultsMaxHeight = false;

    this.predictiveSearchResults.removeAttribute(
      'style'
    );
  }
}

if (
  !customElements.get(
    'predictive-search'
  )
) {
  customElements.define(
    'predictive-search',
    PredictiveSearch
  );
}